import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  MapPin, Trash2, Edit2, X, Save, Zap, Copy, Check, Navigation, Building2, User, LocateFixed, Share2
} from "lucide-react";
import { toast } from "sonner";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";

const EMPTY = { name: "", address: "", phone: "", contact_type: "company", country: "Ethiopia", notes: "" };

const COUNTRIES = ["Ethiopia", "China", "USA", "Other"];
const COUNTRY_COLORS = {
  Ethiopia: "text-[#10b981] bg-[#10b981]/10",
  China: "text-red-400 bg-red-400/10",
  USA: "text-[#60a5fa] bg-[#60a5fa]/10",
  Other: "text-white/60 bg-white/5",
};

const mapsUrl = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const AddressesPage = () => {
  const { API, authHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickName, setQuickName] = useState("");
  const [quickAddress, setQuickAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [copiedId, setCopiedId] = useState(null);
  const [countryFilter, setCountryFilter] = useState("all");
  const [locating, setLocating] = useState(false);

  const filtered = entries.filter(e => countryFilter === "all" || e.country === countryFilter);

  // Grab the phone's GPS position and turn it into a readable address
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("This device does not support location");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();
          const pretty = data.display_name || `${latitude}, ${longitude}`;
          setQuickAddress(pretty);
          toast.success("Current location added — now enter a name and Save");
        } catch {
          setQuickAddress(`${latitude}, ${longitude}`);
          toast.success("Location coordinates added — now enter a name and Save");
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error("Location permission denied — allow location access and try again");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const fetchData = () => {
    axios.get(`${API}/trade/addresses`, { headers: authHeader() })
      .then(res => setEntries(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const quickSave = async () => {
    if (!quickName.trim() || !quickAddress.trim()) {
      toast.error("Enter the name and address");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/trade/addresses`, {
        ...EMPTY, name: quickName.trim(), address: quickAddress.trim()
      }, { headers: authHeader() });
      setEntries(prev => [res.data, ...prev]);
      setQuickName("");
      setQuickAddress("");
      toast.success(`${res.data.name} saved`);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const copyAddress = async (c) => {
    try {
      await navigator.clipboard.writeText(c.address);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {}
  };

  const openEdit = (c) => {
    setEditModal(c);
    setForm({ ...EMPTY, ...c });
  };

  const saveEdit = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      toast.error("Name and address are required");
      return;
    }
    try {
      await axios.put(`${API}/trade/addresses/${editModal.id}`, form, { headers: authHeader() });
      toast.success("Address updated");
      setEditModal(null);
      fetchData();
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await axios.delete(`${API}/trade/addresses/${id}`, { headers: authHeader() });
      toast.success("Address deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Address Book</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Important Addresses</h1>
          <p className="text-white/50 mt-2">Companies and people you visit — one tap opens Google Maps</p>
        </motion.div>

        {/* Quick add */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-8"
          data-testid="address-quick-add"
        >
          <h3 className="text-sm uppercase tracking-widest trade-gold mb-4 flex items-center gap-2">
            <Zap size={14} /> Quick Save
          </h3>
          <div className="flex flex-col md:flex-row gap-3">
            <Input
              value={quickName}
              onChange={e => setQuickName(e.target.value)}
              placeholder="Company or person name"
              className="trade-input h-12 rounded-sm flex-1 text-base"
              data-testid="quick-address-name"
            />
            <Input
              value={quickAddress}
              onChange={e => setQuickAddress(e.target.value)}
              onKeyDown={e => e.key === "Enter" && quickSave()}
              placeholder="Address (street, city, country)"
              className="trade-input h-12 rounded-sm flex-1 text-base"
              data-testid="quick-address-input"
            />
            <Button
              onClick={quickSave}
              disabled={saving}
              className="trade-btn-primary rounded-sm h-12 px-8 text-base"
              data-testid="quick-address-save"
            >
              <MapPin size={18} className="mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
          <button
            onClick={useCurrentLocation}
            disabled={locating}
            className="mt-4 flex items-center gap-1.5 text-xs trade-gold hover:underline"
            data-testid="current-location-btn"
          >
            <LocateFixed size={12} />
            {locating ? "Finding your location..." : "Use my current location as the address"}
          </button>
        </motion.div>

        {/* Country filter */}
        {!loading && entries.length > 0 && (
          <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="country-filter-row">
            {["all", ...COUNTRIES].map(c => (
              <button
                key={c}
                onClick={() => setCountryFilter(c)}
                className={`px-4 py-2 text-xs rounded-sm whitespace-nowrap transition-all ${
                  countryFilter === c
                    ? "bg-[#D4AF37] text-black font-semibold"
                    : "bg-white/5 text-white/60 hover:text-white"
                }`}
                data-testid={`country-filter-${c.toLowerCase()}`}
              >
                {c === "all" ? `All (${entries.length})` : `${c} (${entries.filter(e => e.country === c).length})`}
              </button>
            ))}
          </div>
        )}

        {/* Address list */}
        {loading ? (
          <div className="text-center p-16 text-white/40">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <MapPin className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No addresses yet</h3>
            <p className="text-white/50">Quick-save your first important address above</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <MapPin className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No addresses in {countryFilter}</h3>
            <p className="text-white/50">Add one above or pick another country</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="trade-card rounded-sm p-5"
                data-testid={`address-card-${c.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      {c.contact_type === "person"
                        ? <User className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                        : <Building2 className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                      }
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-serif font-semibold truncate">{c.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] uppercase tracking-widest trade-green">
                          {c.contact_type === "person" ? "Person" : "Company"}
                        </span>
                        {c.country && (
                          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${COUNTRY_COLORS[c.country] || COUNTRY_COLORS.Other}`} data-testid={`country-badge-${c.id}`}>
                            {c.country}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(c)} className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors" data-testid={`edit-address-${c.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-2 text-red-400/60 hover:text-red-400 transition-colors" data-testid={`delete-address-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2 bg-white/5 rounded-sm px-3 py-2 mb-3">
                  <MapPin size={12} className="trade-gold mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-white/80 flex-1" data-testid={`address-text-${c.id}`}>{c.address}</span>
                  <button
                    onClick={() => copyAddress(c)}
                    className="text-white/50 hover:text-[#D4AF37] transition-colors flex-shrink-0"
                    data-testid={`copy-address-${c.id}`}
                    title="Copy address"
                  >
                    {copiedId === c.id ? <Check size={13} className="trade-green" /> : <Copy size={13} />}
                  </button>
                </div>

                {c.notes && <p className="text-[11px] text-white/40 mb-3 line-clamp-2">{c.notes}</p>}

                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                  <a
                    href={mapsUrl(c.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    data-testid={`maps-address-${c.id}`}
                  >
                    <Navigation size={12} /> Google Maps
                  </a>
                  {c.country === "China" ? (
                    <a
                      href={`https://uri.amap.com/search?keyword=${encodeURIComponent(c.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-red-400/10 text-red-400 hover:bg-red-400/20 transition-colors"
                      data-testid={`amap-address-${c.id}`}
                    >
                      <Navigation size={12} /> Amap 高德
                    </a>
                  ) : (
                    <a
                      href={`https://waze.com/ul?q=${encodeURIComponent(c.address)}&navigate=yes`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#33ccff]/10 text-[#33ccff] hover:bg-[#33ccff]/20 transition-colors"
                      data-testid={`waze-address-${c.id}`}
                    >
                      <Navigation size={12} /> Waze
                    </a>
                  )}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`${c.name}\n${c.address}\nMap: ${mapsUrl(c.address)}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition-colors"
                    data-testid={`forward-address-${c.id}`}
                  >
                    <Share2 size={12} /> Forward
                  </a>
                  {c.phone && (
                    <CallPrompt
                      phone={c.phone}
                      name={c.name}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                      testid={`call-address-${c.id}`}
                    />
                  )}
                  <ContactAudioRecordButton
                    phone={c.phone}
                    name={c.name}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    testid={`audio-address-${c.id}`}
                  />
                  {c.phone && (
                    <RecordedCallButton
                      phone={c.phone}
                      name={c.name}
                      contactId={c.id}
                      contactType="address_contact"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                      testid={`record-call-address-${c.id}`}
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Edit modal */}
        {editModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setEditModal(null)}
            data-testid="address-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif font-semibold text-lg">Edit Address</h3>
                <button onClick={() => setEditModal(null)} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="address-modal-close">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Name *</Label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="address-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Type</Label>
                    <Select value={form.contact_type} onValueChange={v => setForm({...form, contact_type: v})}>
                      <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="address-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#142039] border-white/10">
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="person">Person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Country</Label>
                    <Select value={form.country} onValueChange={v => setForm({...form, country: v})}>
                      <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="address-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#142039] border-white/10">
                        {COUNTRIES.map(c => (
                          <SelectItem key={c} value={c} data-testid={`country-option-${c.toLowerCase()}`}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-white/60">Address *</Label>
                    <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Street, city, country" className="trade-input mt-1 h-11 rounded-sm" data-testid="address-address-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-white/60">Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="address-phone-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="Office hours, who to ask for..." className="trade-input mt-1 rounded-sm" data-testid="address-notes-input" />
                </div>
                <Button onClick={saveEdit} className="w-full trade-btn-primary rounded-sm h-12" data-testid="save-address-btn">
                  <Save size={16} className="mr-2" /> Save Address
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressesPage;
